import { useState, useEffect } from "react";
import { Delete, Get, Post, DownloadFile } from "../../../../Services/Server";
import Url from "../../../../Services/Url";
import { toast } from "react-toastify";
import Head from "../../../../Components/Head/Head";
import Svg from "../../../../Components/Svg/Svg";
import SvgOptions from "../../../../Components/Svg/SvgOptions";
import ImgThumb from "../../../../Components/ImgThumb/ImgThumb";
import img from "../../../../Assets/Images/userImage.png";
import css from "../../../AppUser/Engineers/Details/Details.module.css";
import CertModal from "../../../AppUser/Engineers/Details/CertModal";
import { ModalAction } from "../../../../Routes/NavRouter";
import { FileText } from "lucide-react";

const formatDate = (raw) => {
  if (!raw) return raw;
  if (typeof raw === "string" && raw.length === 8)
    return `${raw.substring(0, 4)}-${raw.substring(4, 6)}-${raw.substring(6, 8)}`;
  return raw;
};

const Left = ({ state }) => {
  const { staffBiodata } = state;
  const [certs, setCerts] = useState([]);
  const [showCerts, setShowCerts] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCert, setEditingCert] = useState(null);
  const [previewCert, setPreviewCert] = useState(null);
  const [previewType, setPreviewType] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadDocuments = () => {
    Get(Url.staff.getDocuments(staffBiodata.id))
      .then((resp) => {
        setCerts(
          resp.map((doc) => ({
            id: doc.id,
            name: doc.docName,
            status: doc.docStatus,
            expirationDate: formatDate(doc.docExpirationDate),
            fileId: doc.docId,
            fileType: doc.docType || null, // if backend provides MIME type
          }))
        );
      })
      .catch(() => setCerts([]));
  };

  useEffect(() => {
    loadDocuments();
  }, []);

  const handleSaveCert = (payloadFromModal) => {
    Post(Url.staff.saveDocument(), payloadFromModal)
      .then(() => {
        toast.success("Certification saved");
        loadDocuments();
      })
      .catch(() => toast.error("Failed to save certification"));

    setModalOpen(false);
    setEditingCert(null);
  };

  const handleDelete = (certId) => {
    ModalAction.confirm({
      title: "Confirm Delete",
      text: "Are you sure you want to delete this certification?",
      exitText: "Exit",
      okText: "Delete",
      onOk: () => {
        Delete(Url.staff.deleteDocument(certId))
          .then(() => {
            toast.success("Certification deleted");
            loadDocuments();
          })
          .catch(() => toast.error("Failed to delete certification"));
      },
    });
  };

const previewFile = async (fileId) => {
  setPreviewCert(fileId);
  setLoading(true);

  try {
    const blob = await DownloadFile(fileId); // uses axios + auth
    const type = blob.type; // MIME type
    setPreviewType(type);
  } catch (err) {
    console.error("Failed to fetch file type", err);
    setPreviewType("unknown");
  } finally {
    setLoading(false);
  }
};


  return (
    <div className={css.Left}>
      <Head title={staffBiodata.firstname + " " + staffBiodata.lastname} state={state} />

      <div className={css.Main}>
        <ImgThumb size={200}>{img}</ImgThumb>
        <div className={css.About}>
          <h3>About</h3>
          <div>Staff Number: {staffBiodata.staffNumber}</div>
          <div>First name: {staffBiodata.firstname}</div>
          <div>Last name: {staffBiodata.lastname}</div>
          <div>Role: {staffBiodata.appRoles.map((v) => v.name).join(", ")}</div>
          <div>Email: {staffBiodata.email}</div>
          <div>Designation: {staffBiodata.designation?.name}</div>
        </div>
      </div>

      <div className={css.FormsContainer}>
        <div className={css.BoxTitle} onClick={() => setShowCerts((s) => !s)}>
          <Svg className={css.svg}>
            {showCerts ? SvgOptions.ArrowDownFill : SvgOptions.ArrowSideFill}
          </Svg>
          Certifications
        </div>

        {showCerts && (
          <div className={css.InputsContainer}>
            {certs.length > 0 && (
              <table className={css.CertTable}>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Expiration Date</th>
                    <th>Status</th>
                    <th>Document</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {certs.map((cert) => {
                    const isExpired =
                      cert.expirationDate && new Date(cert.expirationDate) < new Date();

                    return (
                      <tr key={cert.id} className={isExpired ? css.ExpiredRow : ""}>
                        <td>{cert.name}</td>
                        <td>{cert.expirationDate}</td>
                        <td>
                          <div
                            className={`${css.Status} ${
                              isExpired ? css.StatusExpired : css.StatusActive
                            }`}
                          >
                            <span className={css.StatusDot}></span>
                            {isExpired ? "Expired" : "Active"}
                          </div>
                        </td>
                        <td>
                          {cert.fileId ? (
                            <button
                              className={css.ViewBtn}
                              onClick={() => previewFile(cert.fileId)}
                            >
                              View
                            </button>
                          ) : (
                            "-"
                          )}
                        </td>
                        <td className={css.icons}>
                          <Svg
                            onClick={() => {
                              setEditingCert(cert);
                              setModalOpen(true);
                            }}
                            className={css.ActionIcon}
                          >
                            {SvgOptions.Edit4}
                          </Svg>
                          <Svg
                            onClick={() => handleDelete(cert.id)}
                            className={css.ActionIcon}
                          >
                            {SvgOptions.Delete}
                          </Svg>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}

            <button
              className={css.UploadCertBtn}
              onClick={() => {
                setEditingCert(null);
                setModalOpen(true);
              }}
            >
              <Svg className={css.svg}>{SvgOptions.Add}</Svg> Add New Certification
            </button>
          </div>
        )}
      </div>

      {modalOpen && (
        <CertModal
          onClose={() => {
            setModalOpen(false);
            setEditingCert(null);
          }}
          onSave={handleSaveCert}
          existing={editingCert}
          staffBiodata={staffBiodata}
        />
      )}

      {previewCert && (
        <div className={css.PreviewOverlay} onClick={() => setPreviewCert(null)}>
          <div
            className={css.PreviewContent}
            onClick={(e) => e.stopPropagation()}
          >
            <span className={css.CloseIcon} onClick={() => setPreviewCert(null)}>
              ×
            </span>
            {loading && <div className={css.Loader}>Loading...</div>}

            {previewType?.startsWith("image/") ? (
              <img
                src={Url.img.get(previewCert)}
                alt="Certificate"
                onLoad={() => setLoading(false)}
              />
            ) : previewType === "application/pdf" ? (
              <iframe
                src={Url.img.get(previewCert)}
                title="Certificate Preview"
                width="100%"
                height="500px"
                onLoad={() => setLoading(false)}
              />
            ) : (
              <div className={css.FilePreview}>
                <FileText size={40} />
                <div className={css.FileLabel}>File Preview</div>
                <a
                  href={Url.img.get(previewCert)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Open File
                </a>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Left;
